import { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";

const CATEGORY_LABEL = {
  aptitude: "General Aptitude",
  reasoning: "Reasoning",
  mathematics: "Mathematics",
  english: "English",
  computer: "Computer Basics",
  current_affairs: "Current Affairs",
  etiquette: "Social Etiquette",
};

export const QuizRunner = ({ questions, onSubmit, loading }) => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const q = questions[idx];
  const total = questions.length;
  const answered = Object.keys(answers).length;
  const isLast = idx === total - 1;

  const choose = (i) => setAnswers((a) => ({ ...a, [q.id]: i }));

  return (
    <div data-testid="assessment-quiz" className="bg-white border border-slate-200 rounded-3xl p-7 md:p-9 shadow-xl shadow-slate-200/50">
      <div className="flex items-center justify-between text-xs font-bold uppercase tracking-[0.18em] text-[#64748B]">
        <span data-testid="quiz-progress-label">Question {idx + 1} of {total}</span>
        <span className="text-[#00B37E]">{CATEGORY_LABEL[q.category] || q.category}</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#00B37E] to-[#7CF0BD] transition-all duration-500" style={{ width: `${((idx + 1) / total) * 100}%` }} />
      </div>

      <h2 data-testid="quiz-question" className="mt-7 font-heading text-xl md:text-2xl font-bold text-[#0A192F] leading-snug">{q.question}</h2>

      <div className="mt-6 grid gap-3">
        {q.options.map((opt, i) => {
          const selected = answers[q.id] === i;
          return (
            <button
              key={i}
              type="button"
              data-testid={`quiz-option-${i}`}
              onClick={() => choose(i)}
              className={`flex items-center gap-4 text-left rounded-2xl border-2 px-5 py-4 transition-all duration-200 ${
                selected ? "border-[#00B37E] bg-[#00B37E]/8 shadow-md shadow-[#00B37E]/15" : "border-slate-200 hover:border-[#0A192F]/40 hover:-translate-y-0.5"
              }`}
            >
              <span className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${selected ? "bg-[#00B37E] text-white" : "bg-slate-100 text-[#0A192F]"}`}>
                {String.fromCharCode(65 + i)}
              </span>
              <span className="text-[#0A192F] text-base">{opt}</span>
              {selected && <CheckCircle2 className="w-5 h-5 text-[#00B37E] ml-auto flex-shrink-0" />}
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button type="button" variant="ghost" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)} data-testid="quiz-prev-btn" className="rounded-full h-11 px-5 text-[#0A192F]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Previous
        </Button>
        <div className="flex items-center gap-3 sm:ml-auto">
          <span className="text-xs text-[#64748B]" data-testid="quiz-answered-count">{answered}/{total} answered</span>
          {isLast ? (
            <Button type="button" disabled={answered < total || loading} onClick={() => onSubmit(answers)} data-testid="quiz-submit-btn" className="bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-11 px-7">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {loading ? "Calculating..." : "Submit & Get Discount"}
            </Button>
          ) : (
            <Button type="button" disabled={answers[q.id] === undefined} onClick={() => setIdx((i) => i + 1)} data-testid="quiz-next-btn" className="bg-[#0A192F] hover:bg-[#0F2648] text-white font-semibold rounded-full h-11 px-7">
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
